"use client"

/**
 * Guarded browser-storage reads.
 *
 * Two things can go wrong with `JSON.parse(localStorage.getItem(k) ?? "{}")`:
 *   1. the stored value is corrupt / not JSON → SyntaxError
 *   2. the browser blocks site data → SecurityError on access itself
 *
 * Either one thrown inside a mount effect replaces the page with an error
 * boundary. A cached convenience value is never worth a broken page, so these
 * helpers always degrade to a fallback instead of throwing.
 *
 * (A global shim in app/layout.tsx also swaps in an in-memory store when the
 * browser denies storage; these helpers additionally cover corrupt contents.)
 */

export function readStoredJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return fallback
    const parsed = JSON.parse(raw)
    return (parsed ?? fallback) as T
  } catch {
    return fallback
  }
}

/** The cached `ze_user` blob. Always an object — never throws. */
export function readStoredUser(): Record<string, any> {
  const v = readStoredJson<Record<string, any>>("ze_user", {})
  return v && typeof v === "object" && !Array.isArray(v) ? v : {}
}
